import {inject, Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {OrgDto} from "../dto/org.dto";
import {UrlBuilder} from "../item/url.item";
import {WrapperDto} from "../dto/wrapper.dto";
import {map} from "rxjs/operators";
import {CatalogDto} from "../dto/catalog.dto";
import {GoodsGroupDto} from "../dto/goods-group.dto";
import {VoidDto} from "../dto/void.dto";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {Observable, tap} from "rxjs";
import {Additionals} from "../item/additionals";
import {ArticleDto} from "../dto/article.dto";
import {SearchArticleRes} from "../dto/search/search-article.res";
import {UrlDto} from "../item/url.dto";

@Injectable({providedIn: 'root'})
export class ArticleRepo {

  private readonly articleRead: string = '/v1/org/{orgId}/article/{id}';
  private readonly articleWrite: string = '/v1/org/{orgId}/article';
  private readonly articleSearch: string = '/v1/org/{orgId}/article/search';
  private readonly articleDelete: string = '/v1/org/{orgId}/article/{id}/delete';
  private readonly catalogAllRead: string = '/v1/org/{orgId}/article/catalog/all';
  private readonly catalogCustomRead: string = '/v1/org/{orgId}/article/catalog/custom';
  private readonly goodsGroupAllRead: string = '/v1/org/{orgId}/article/goodsgroup/all';
  private readonly goodsGroupCustomRead: string = '/v1/org/{orgId}/article/goodsgroup/custom';

  private readonly http: HttpClient = inject(HttpClient);

  public getById(org: OrgDto, articleId: string, add?: Additionals): Observable<WrapperDto<ArticleDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.articleRead).addAdditionals(add)
      .addParam('orgId', org.id).addParam('id', articleId).build();
    return this.http.get<WrapperDto<ArticleDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public updateArticle(org: OrgDto, article: ArticleDto): Observable<WrapperDto<ArticleDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.articleWrite).addParam('orgId', org.id).build();
    return this.http.post<WrapperDto<ArticleDto>>(url.getUrl(), article, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public deleteArticle(org: OrgDto, articleId: string): Observable<WrapperDto<VoidDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.articleDelete)
      .addParam('orgId', org.id).addParam('id', articleId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Search
  // ##########################################################################

  public search(org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchArticleRes>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.articleSearch).addParam('orgId', org.id).build();
    return this.http.post<WrapperDto<SearchArticleRes>>(url.getUrl(), request, url.getOptions()).pipe(
      tap((res) => res.dataType = res.dataType === 'SearchResultDto' ? 'SearchArticleRes' : res.dataType),
      map((res) => new WrapperDto(res))
    );
  }

  // ##########################################################################
  // #### Catalogs
  // ##########################################################################

  public readAllCatalogs(org: OrgDto): Observable<WrapperDto<CatalogDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.catalogAllRead).addParam('orgId', org.id).build();
    return this.http.get<WrapperDto<CatalogDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public readCustomCatalogs(org: OrgDto): Observable<WrapperDto<CatalogDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.catalogCustomRead).addParam('orgId', org.id).build();
    return this.http.get<WrapperDto<CatalogDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### GoodsGroups
  // ##########################################################################

  public readAllGoodsGroup(org: OrgDto): Observable<WrapperDto<GoodsGroupDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.goodsGroupAllRead).addParam('orgId', org.id).build();
    return this.http.get<WrapperDto<GoodsGroupDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public readCustomGoodsGroup(org: OrgDto): Observable<WrapperDto<GoodsGroupDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.goodsGroupCustomRead).addParam('orgId', org.id).build();
    return this.http.get<WrapperDto<GoodsGroupDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }
}
